import { getHeaders } from "lib/bridge/helpers";

import { bridgeRequest, request } from "../axios";
import { encodeQueryData, handleApiError } from "../helper";
import {
  AssetChainTokenAllowanceResponse,
  AssetChainAddTransaction,
  AssetChainGetTokenAllowanceParams,
  AssetChainGetTokenBalancesBody,
  AssetChainToken,
  AssetChainTokenBalancesResponse,
  AssetChainGetUserTransactionDto,
  AssetChainApiGetUserTransactionDto,
  AssetChainTransaction,
  PaginatedApiResponse,
  AssetChainGetCurrentBlocksResponse,
  AssetChainGetCurrentBlocksParams,
  ClaimTokenBridgeRequestParams,
  ClaimTokenBridgeResponse,
} from "./index.dto";
import { AssetChainApiResponse } from "./index.dto";

const KEYS = {
  TOKENS: "tokens",
  TRANSACTIONS: "transactions",
  BLOCKS: "blockchain/block",
  CLAIM: "asset-chain/bridge/claim",
  CLIENT_KEY: "client",
};

export async function getClientKey() {
  try {
    const { data } = await bridgeRequest.get<AssetChainApiResponse<string>>(KEYS.CLIENT_KEY);
    return data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getBridgeTokens(clientKey: string) {
  const headers = await getHeaders(clientKey, "GET", `/${KEYS.TOKENS}`, "");
  try {
    const { data } = await bridgeRequest.get<AssetChainApiResponse<AssetChainToken[]>>(KEYS.TOKENS, { headers });
    return data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getBridgeTokenBalance(body: AssetChainGetTokenBalancesBody, clientKey: string) {
  const headers = await getHeaders(clientKey, "POST", `/${KEYS.TOKENS}`, JSON.stringify(body));
  try {
    const { data } = await bridgeRequest.post<AssetChainApiResponse<AssetChainTokenBalancesResponse>>(
      KEYS.TOKENS,
      body,
      { headers }
    );
    return data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getBridgeTokenAllowance(params: AssetChainGetTokenAllowanceParams, clientKey: string) {
  const query = params ? encodeQueryData(params) : "";
  const path = `${KEYS.TOKENS}/token-allowance?${query}`;
  const headers = await getHeaders(clientKey, "GET", `/${path}`, "");
  try {
    const { data } = await bridgeRequest.get<AssetChainApiResponse<AssetChainTokenAllowanceResponse>>(`${path}`, {
      headers,
    });
    return data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function addUserTransaction(transaction: AssetChainAddTransaction, clientKey: string) {
  const headers = await getHeaders(clientKey, "POST", `/${KEYS.TRANSACTIONS}`, JSON.stringify(transaction));
  try {
    const { data } = await bridgeRequest.post<AssetChainApiResponse<any>>(`${KEYS.TRANSACTIONS}`, transaction, {
      headers,
    });
    const _transaction: AssetChainTransaction = data.data.data;
    return _transaction;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function getUserTransactions(params: AssetChainGetUserTransactionDto, clientKey: string) {
  const queryParams: AssetChainApiGetUserTransactionDto = {
    userAddress: params.userAddress,

    page: params.page.toString(),
    limit: params.limit.toString(),
  };
  if (params.chainIds) {
    queryParams.chainIds = params.chainIds.join(",");
  }
  if (params.fulfilled) {
    queryParams.fulfilled = params.fulfilled.toString();
  }
  if (params.forceSync) {
    queryParams.forceSync = params.forceSync.toString();
  }
  if (params.symbol) {
    queryParams.symbol = params.symbol;
  }
  if (params.secondaryAddress) {
    queryParams.secondaryAddress = params.secondaryAddress;
  }
  const path = `${KEYS.TRANSACTIONS}?${encodeQueryData(queryParams)}`;
  const headers = await getHeaders(clientKey, "GET", `/${KEYS.TRANSACTIONS}`, "");
  const { data } = await bridgeRequest.get<AssetChainApiResponse<PaginatedApiResponse<AssetChainTransaction>>>(
    `${path}`,
    { headers }
  );
  return data;
}

export async function getCurrentBlocks(params: AssetChainGetCurrentBlocksParams, clientKey: string) {
  const path = `${KEYS.BLOCKS}?${encodeQueryData(params)}`;
  const headers = await getHeaders(clientKey, "GET", `/${KEYS.BLOCKS}`, "");
  const { data } = await bridgeRequest.get<AssetChainApiResponse<AssetChainGetCurrentBlocksResponse>>(`${path}`, {
    headers,
  });
  return data;
}

export async function claimBridgeToken(params: ClaimTokenBridgeRequestParams) {
  const { data } = await request.get<AssetChainApiResponse<ClaimTokenBridgeResponse>>(
    `${KEYS.CLAIM}?${encodeQueryData(params)}`
  );
  return data;
}
